import { useMemo } from "react";
import { Activity, WifiOff, Wifi } from "lucide-react";

import { useExtractionLive } from "../../../hooks/useExtractionLive";
import { ExtractionStageChip } from "./ExtractionStageChip";
import {
  eventDocumentId,
  type ExtractionStageEvent,
} from "../../../types/documents";

interface InFlight {
  documentId: string;
  latestEvent: ExtractionStageEvent;
}

function aggregateInFlight(events: ExtractionStageEvent[]): InFlight[] {
  // For each documentId keep the most recent event. Drop the row once the
  // backend has reported a terminal-looking status — those live in the
  // SmartInbox below.
  const byDoc = new Map<string, ExtractionStageEvent>();
  for (const evt of events) {
    const id = eventDocumentId(evt);
    if (!id) continue;
    if (!byDoc.has(id)) byDoc.set(id, evt);
  }
  return Array.from(byDoc.entries())
    .map(([documentId, latestEvent]) => ({ documentId, latestEvent }))
    .filter(({ latestEvent }) => {
      const u = (latestEvent.status || "").toUpperCase();
      // Keep anything that's clearly mid-flight; drop fully-terminal docs.
      if (u === "OUTPUT_COMPLETED" || u === "WORKFLOW_COMPLETED") return false;
      return true;
    })
    .slice(0, 12);
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return "just now";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "just now";
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

export function LiveExtractionQueue() {
  const { events, connected, error } = useExtractionLive({ bufferSize: 200 });
  const inFlight = useMemo(() => aggregateInFlight(events), [events]);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white">
      <header className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-600" />
          <h2 className="text-sm font-semibold text-gray-900">In flight</h2>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
            {inFlight.length}
          </span>
        </div>
        <div
          className={`flex items-center gap-1 text-xs ${
            connected ? "text-emerald-600" : "text-rose-600"
          }`}
          title={error ?? (connected ? "Live updates connected" : "Reconnecting…")}
        >
          {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {connected ? "Live" : "Offline"}
        </div>
      </header>

      {inFlight.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">
          {connected
            ? "No documents currently extracting. Drop a file above to get started."
            : "Waiting for live connection…"}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {inFlight.map(({ documentId, latestEvent }) => (
            <li
              key={documentId}
              className="flex items-center justify-between gap-4 px-5 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm text-gray-700">
                  {documentId}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <ExtractionStageChip status={latestEvent.status} />
                  <span>{relativeTime(latestEvent.at)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
